import { createFeatureSelector, createSelector } from "@ngrx/store";
import { EditProcedureState, FactoryAsset, MappingAssetToProcedure, MappingAssetToResource, ResourceStatus, Procedure, ProcedureInstance, ProcedureStepRedline, ProcedureStepRedlineStatus, ProcedureVersion, ProcedureVersionStatus, Resource, SaveStatus, ScheduledProcedure, toEntityDict, ViewProcedureState, FreightCarrier, SoftwareTask, Customer, Vendor, PurchaseRequestList, PurchaseRequest, Contact, SoftwareRequest, AssetHistoryEvent, PurchaseOrder, CapitalizationProject } from "@prism/prism-shared";
import { PrismCommonState } from "./common.reducer";
import { getSoftwareTasks } from "../automation/automation.selectors";

export const prismCommonState = createFeatureSelector<PrismCommonState>('prismCommon')
export const getLoadingState = createSelector(
  prismCommonState,
  (state: PrismCommonState): number => state.loadingState
);

export const getError = createSelector(
  prismCommonState,
  (state: PrismCommonState): any => state.error
);

export const getResources = createSelector(
  prismCommonState,
  (state: PrismCommonState): Resource[] => state.resources
);

export const getProcedures = createSelector(
  prismCommonState,
  (state: PrismCommonState): Procedure[] => state.procedures
);

export const getProcedureVersions = createSelector(
  prismCommonState,
  (state: PrismCommonState): ProcedureVersion[] => {
    return state.procedureVersions
  }
);

export const getProcedureInstances = createSelector(
  prismCommonState,
  (state: PrismCommonState): ProcedureInstance[] => state.procedureInstances
);

export const getScheduledProcedures = createSelector(
  prismCommonState,
  (state: PrismCommonState): ScheduledProcedure[] => state.scheduledProcedures
);

export const getViewProcedureState = createSelector(
  prismCommonState,
  (state: PrismCommonState): ViewProcedureState => state.viewProcedureState
);

export const getEditProcedureState = createSelector(
  prismCommonState,
  (state: PrismCommonState): EditProcedureState => state.editProcedureState
);

export const getFactoryAssets = createSelector(
  prismCommonState,
  (state: PrismCommonState): FactoryAsset[] => state.factoryAssets
)
export const getAssetHistoryEvents = createSelector(
  prismCommonState,
  (state: PrismCommonState): AssetHistoryEvent[] => state.assetHistoryEvents
)

export const getMappingAssetToResources = createSelector(
  prismCommonState,
  (state: PrismCommonState): MappingAssetToResource[] => state.mappingAssetToResources
)

export const getMappingAssetToProcedures = createSelector(
  prismCommonState,
  (state: PrismCommonState): MappingAssetToProcedure[] => state.mappingAssetToProcedures
)

export const getFreightCarriers = createSelector(
  prismCommonState,
  (state: PrismCommonState): FreightCarrier[] => state.freightCarriers
)

export const getCapitalizationProjects = createSelector(
  prismCommonState,
  (state: PrismCommonState): CapitalizationProject[] => state.capitalizationProjects
)

export const getCapitalizationProjectDict = createSelector(
  getCapitalizationProjects,
  (projects: CapitalizationProject[]): Record<string, CapitalizationProject> => toEntityDict<CapitalizationProject>(projects)
)

export const getCustomers = createSelector(
  prismCommonState,
  (state: PrismCommonState): Customer[] => state.customers
)

export const getVendors = createSelector(
  prismCommonState,
  (state: PrismCommonState): Vendor[] => state.vendors
)

export const getPurchaseRequestLists = createSelector(
  prismCommonState,
  (state: PrismCommonState): PurchaseRequestList[] => state.purchaseRequestLists
)

export const getPurchaseRequests = createSelector(
  prismCommonState,
  (state: PrismCommonState): PurchaseRequest[] => state.purchaseRequests
)

export const getContacts = createSelector(
  prismCommonState,
  (state: PrismCommonState): Contact[] => state.contacts
)

export const getSoftwareRequests = createSelector(
  prismCommonState,
  (state: PrismCommonState): SoftwareRequest[] => state.softwareRequests
)

// above are selectors that ONLY use prismCommonState
// =========================================================
// below are selectors that are built with selectors above

export const getActiveResources = createSelector(
  getResources,
  (resources: Resource[]): Resource[] => resources.filter(resource => resource.status === ResourceStatus.ACTIVE)
)

export function getPrismResourceById(resourceId: string) {
  return createSelector(
    getResources,
    (resources: Resource[]): Resource => {
      return resources.find(resource => resource.id === resourceId) as Resource;
    }
  )
};

export function getScheduledProceduresByProcedureId(procedureId: string) {
  return createSelector(
    getScheduledProcedures,
    (scheduledProcedures: ScheduledProcedure[]): ScheduledProcedure[] => {
      return scheduledProcedures.filter(sp => sp.procedureId === procedureId);
    }
  )
}

export const getProcedureIdToReleasedProcedureVersionDict = createSelector(
  getProcedureVersions,
  (versions: ProcedureVersion[]): Record<string, ProcedureVersion> => {
    return versions
      .filter(version => version.status === ProcedureVersionStatus.Released)
      .reduce((dict: Record<string, ProcedureVersion>, version) => {
        dict[version.procedureId] = version;
        return dict;
      }, {})

  }
);

export const getResourceDict = createSelector(
  getResources,
  (resources: Resource[]): Record<string, Resource> => toEntityDict<Resource>(resources)
);

export const getSortedResources = createSelector(
  getResources,
  (resources: Resource[]): Resource[] => [...resources].sort((a, b) => a.resourceNumber - b.resourceNumber)
);

export const getProcedureDict = createSelector(
  getProcedures,
  (procedures: Procedure[]): Record<string, Procedure> => toEntityDict<Procedure>(procedures)
);

export const getFactoryAssetDict = createSelector(
  getFactoryAssets,
  (assets: FactoryAsset[]): Record<string, FactoryAsset> => toEntityDict<FactoryAsset>(assets)
);

export const getProcedureVersionDict = createSelector(
  getProcedureVersions,
  (versions: ProcedureVersion[]): Record<string, ProcedureVersion> => toEntityDict<ProcedureVersion>(versions)
);

export const getReleasedProcedureVersions = createSelector(
  getProcedureVersions,
  (procedureVersions: ProcedureVersion[]): ProcedureVersion[] => {
    return procedureVersions.filter(procedureVersion => procedureVersion.status === ProcedureVersionStatus.Released)
  }
);

export const getDraftProcedureVersions = createSelector(
  getProcedureVersions,
  (procedureVersions: ProcedureVersion[]): ProcedureVersion[] => {
    return procedureVersions.filter(procedureVersion => procedureVersion.status === ProcedureVersionStatus.Draft)
  }
);

export const getViewProcedureSelectedProcedure = createSelector(
  getViewProcedureState,
  (state: ViewProcedureState): Procedure => state.selectedProcedure
);

export const getViewProcedureSelectedProcedureVersions = createSelector(
  getViewProcedureState,
  (state: ViewProcedureState): ProcedureVersion[] => state.selectedProcedureVersions
);

export const getViewProcedureSelectedProcedureVersionId = createSelector(
  getViewProcedureState,
  (state: ViewProcedureState): string => state.selectedProcedureVersionId
);

export const getViewProcedureSelectedProcedureVersion = createSelector(
  getViewProcedureSelectedProcedureVersionId,
  getViewProcedureSelectedProcedureVersions,
  (selectedProcedureVersionId: string, versions: ProcedureVersion[]): ProcedureVersion => {
    return versions.find(version => version.id === selectedProcedureVersionId) as ProcedureVersion
  }
);

export const getEditProcedureProcedure = createSelector(
  getEditProcedureState,
  (state: EditProcedureState): Procedure => state.procedure
)

export const getEditProcedureProcedureVersion = createSelector(
  getEditProcedureState,
  (state: EditProcedureState): ProcedureVersion => state.procedureVersion
)

export const getEditProcedureProcedureStepRedlines = createSelector(
  getEditProcedureState,
  (state: EditProcedureState): ProcedureStepRedline[] => state.procedureStepRedlines
)

export const getEditProcedureSaveStatus = createSelector(
  getEditProcedureState,
  (state: EditProcedureState): SaveStatus => state.saveStatus
)

export const getEditProcedureExpandedRedlineIds = createSelector(
  getEditProcedureState,
  (state: EditProcedureState): string[] => state.expandedRedlineIds
)

export const getEditProcedureSelectedRedlinePanelStepId = createSelector(
  getEditProcedureState,
  (state: EditProcedureState): string | null => state.selectedRedlinePanelStepId
)

export const getEditProcedureStepIdToRedlinesDict = createSelector(
  getEditProcedureProcedureStepRedlines,
  (redlines: ProcedureStepRedline[]): Record<string, ProcedureStepRedline[]> => {
    return redlines.reduce((dict: Record<string, ProcedureStepRedline[]>, redline) => {
      if (redline.stepId in dict) {
        dict[redline.stepId].push(redline)
      } else {
        dict[redline.stepId] = [redline]
      }
      return dict;
    }, {});
  }
)

export const getEditProcedureSelectedRedlines = createSelector(
  getEditProcedureSelectedRedlinePanelStepId,
  getEditProcedureStepIdToRedlinesDict,
  (stepId: string | null, dict: Record<string, ProcedureStepRedline[]>): ProcedureStepRedline[] => {
    if (!stepId || !dict[stepId]) {
      return [];
    }
    const redlines = dict[stepId];
    return [...redlines].sort((
      a: ProcedureStepRedline,
      b: ProcedureStepRedline
    ) => {
      // 1) Map statuses to priority: Pending(1) -> Rejected(2) -> Incorperated(3).
      //    This ensures Pending sorts before Rejected, which sorts before Incorperated.
      const getStatusPriority = (status: ProcedureStepRedlineStatus): number => {
        switch (status) {
          case ProcedureStepRedlineStatus.Pending:
            return 1;
          case ProcedureStepRedlineStatus.Rejected:
            return 2;
          case ProcedureStepRedlineStatus.Incorperated:
            return 3;
        }
      };

      const statusPriorityA = getStatusPriority(a.status);
      const statusPriorityB = getStatusPriority(b.status);

      // If statuses differ, sort by their priority.
      if (statusPriorityA !== statusPriorityB) {
        return statusPriorityA - statusPriorityB; // ascending
      }

      // 2) If same status, we sort differently based on that status:

      // PENDING => sort by createdOn DESC (most recent first).
      if (a.status === ProcedureStepRedlineStatus.Pending) {
        return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
      }

      // REJECTED or INCORPERATED => sort by addressedOn DESC (most recent first).
      // *In theory, addressedOn might be null if not yet addressed. 
      //  If needed, handle nulls by pushing them down or up. Here we treat null as "older than any date".
      const addressedOnA = a.addressedOn ? new Date(a.addressedOn).getTime() : 0;
      const addressedOnB = b.addressedOn ? new Date(b.addressedOn).getTime() : 0;
      return addressedOnB - addressedOnA;
    })
  }
);

export function getReleasedProcedureVersionIdByProcedureId(procedureId: string) {
  return createSelector(
    getReleasedProcedureVersions,
    (productVersions: ProcedureVersion[]): string => {
      const releasedProcedure: ProcedureVersion = productVersions.find(version => version.procedureId === procedureId) as ProcedureVersion
      return releasedProcedure.id
    }
  )
}

export function getReleasedProcedureVersionsByProcedureIds(procedureIds: string[]) {
  return createSelector(
    getReleasedProcedureVersions,
    (procedureVersions: ProcedureVersion[]): ProcedureVersion[] => {
      return procedureVersions.filter(version => procedureIds.some(id => id === version.procedureId))
    }
  )
}

export function getFactoryAssetById(assetId: string) {
  return createSelector(
    getFactoryAssets,
    (assets: FactoryAsset[]): FactoryAsset => {
      return assets.find(asset => asset.id === assetId) as FactoryAsset
    }
  )
}

export function getResourcesByIds(resourceIds: string[]) {
  return createSelector(
    getResources,
    (resources: Resource[]): Resource[] => {
      return resources.filter(resource => resourceIds.some(id => id === resource.id))
    }
  )
}


export function getMappingAssetToResourcesByAssetId(assetId: string) {
  return createSelector(
    getMappingAssetToResources,
    (mappingAssetToResources: MappingAssetToResource[]): MappingAssetToResource[] => {
      return mappingAssetToResources.filter(mapping => mapping.assetId === assetId)
    }
  )
}

export function getResourcesByFactoryAssetId(assetId: string) {
  return createSelector(
    getResources,
    getMappingAssetToResourcesByAssetId(assetId),
    (resources: Resource[], mappingAssetToResources: MappingAssetToResource[]): Resource[] => {
      const resourceIds = mappingAssetToResources.map(mapping => mapping.resourceId)
      return resources.filter(resource => resourceIds.some(id => id === resource.id))
    }
  )
}

export function getMappingAssetToProceduresByAssetId(assetId: string) {
  return createSelector(
    getMappingAssetToProcedures,
    (mappingAssetToProcedures: MappingAssetToProcedure[]): MappingAssetToProcedure[] => {
      return mappingAssetToProcedures.filter(mapping => mapping.assetId === assetId)
    }
  )
}

export function getProcedureVersionsByAssetId(assetId: string) {
  return createSelector(
    getReleasedProcedureVersions,
    getMappingAssetToProceduresByAssetId(assetId),
    (procedureVersions: ProcedureVersion[], mappingAssetToProcedures: MappingAssetToProcedure[]) => {
      const procedureIds = mappingAssetToProcedures.map(mapping => mapping.procedureId)
      return procedureVersions.filter(version => procedureIds.some(id => id === version.procedureId))
    }
  )
}

export const getFreightCarrierDict = createSelector(
  getFreightCarriers,
  (carriers: FreightCarrier[]): Record<string, FreightCarrier> => toEntityDict<FreightCarrier>(carriers)
)

export function getSoftwareTaskById(taskId: string) {
  return createSelector(
    getSoftwareTasks,
    (tasks: SoftwareTask[]): SoftwareTask => {
      return tasks.find(task => task.id === taskId) as SoftwareTask
    }
  )
}

export function getCustomerById(customerId: string) {
  return createSelector(
    getCustomers,
    (customers: Customer[]): Customer => {
      return customers.find(customer => customer.id === customerId) as Customer;
    }
  )
}

export const getCustomerDict = createSelector(
  getCustomers,
  (customers: Customer[]): Record<string, Customer> => toEntityDict<Customer>(customers)
);

export const getPlasticsCustomers = createSelector(
  getCustomers,
  (customers: Customer[]): Customer[] => customers.filter((customer) => customer.isPlasticsCustomer)
)

export const getMetalsCustomers = createSelector(
  getCustomers,
  (customers: Customer[]): Customer[] => customers.filter((customer) => customer.isMetalsCustomer)
)

export const getC3Customers = createSelector(
  getCustomers,
  (customers: Customer[]): Customer[] => customers.filter((customer) => customer.isC3Customer)
)

export const getPlasticsCustomerDict = createSelector(
  getPlasticsCustomers,
  (customers: Customer[]): Record<string, Customer> => toEntityDict<Customer>(customers)
)

export const getMetalsCustomerDict = createSelector(
  getMetalsCustomers,
  (customers: Customer[]): Record<string, Customer> => toEntityDict<Customer>(customers)
)

export const getC3CustomersDict = createSelector(
  getC3Customers,
  (customers: Customer[]): Record<string, Customer> => toEntityDict<Customer>(customers)
)

export function getVendorById(vendorId: string) {
  return createSelector(
    getVendors,
    (vendors: Vendor[]): Vendor => {
      return vendors.find(vendor => vendor.id === vendorId) as Vendor;
    }
  )
}

export const getPlasticsVendors = createSelector(
  getVendors,
  (vendors: Vendor[]): Vendor[] => vendors.filter((v) => v.isPlasticsVendor)
)

export const getMetalsVendors = createSelector(
  getVendors,
  (vendors: Vendor[]): Vendor[] => vendors.filter((v) => v.isMetalsVendor)
)

export const getC3Vendors = createSelector(
  getVendors,
  (vendors: Vendor[]): Vendor[] => vendors.filter((v) => v.isC3Vendor),
)

export const getAutomationVendors = createSelector(
  getVendors,
  (vendors: Vendor[]): Vendor[] => vendors.filter((v) => v.isAutomationVendor),
)

export const getVendorDict = createSelector(
  getVendors,
  (vendors: Vendor[]): Record<string, Vendor> => toEntityDict<Vendor>(vendors)
);

export const getPlasticsVendorDict = createSelector(
  getPlasticsVendors,
  (vendors: Vendor[]): Record<string, Vendor> => toEntityDict<Vendor>(vendors)
);

export const getMetalsVendorDict = createSelector(
  getMetalsVendors,
  (vendors: Vendor[]): Record<string, Vendor> => toEntityDict<Vendor>(vendors)
);

export const getC3VendorsDict = createSelector(
  getC3Vendors,
  (vendors: Vendor[]): Record<string, Vendor> => toEntityDict<Vendor>(vendors)
)

export const getPurchaseRequestListsDict = createSelector(
  getPurchaseRequestLists,
  (lists: PurchaseRequestList[]): Record<string, PurchaseRequestList> => toEntityDict<PurchaseRequestList>(lists)
)

export function getSoftwareRequestById(requestId: string) {
  return createSelector(
    getSoftwareRequests,
    (requests: SoftwareRequest[]): SoftwareRequest => {
      return requests.find(request => request.id === requestId) as SoftwareRequest
    }
  )
}